import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MbCard } from "@/components/MbCard";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { DollarSign, TrendingUp, TrendingDown } from "lucide-react";

interface PaymentStats {
  total_checkouts: number;
  successful_payments: number;
  failed_payments: number;
  success_rate: number;
  total_revenue: number;
  avg_transaction_value: number;
}

interface DailyData {
  date: string;
  success: number;
  failed: number;
}

export default function PaymentAnalytics() {
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchPaymentStats();
    fetchDailyTrends();
  }, []);
  
  const fetchPaymentStats = async () => {
    try {
      // Last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data, error } = await supabase
        .from('payment_events')
        .select('event_type, amount')
        .gte('created_at', thirtyDaysAgo.toISOString());
      
      if (error) throw error;
      
      if (data) {
        const checkouts = data.filter(e => e.event_type === 'checkout_started').length;
        const successes = data.filter(e => e.event_type === 'payment_success').length;
        const failures = data.filter(e => e.event_type === 'payment_failed').length;
        const revenue = data
          .filter(e => e.event_type === 'payment_success')
          .reduce((sum, e) => sum + (e.amount || 0), 0);
        
        setStats({
          total_checkouts: checkouts,
          successful_payments: successes,
          failed_payments: failures,
          success_rate: checkouts > 0 ? (successes / checkouts) * 100 : 0,
          total_revenue: revenue,
          avg_transaction_value: successes > 0 ? revenue / successes : 0,
        });
      }
    } catch (error) {
      console.error('Error fetching payment stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDailyTrends = async () => {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { data, error } = await supabase
        .from('payment_events')
        .select('created_at, event_type')
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      if (data) {
        // Group by date
        const groupedByDate: Record<string, { success: number; failed: number }> = {};
        
        data.forEach(event => {
          const date = new Date(event.created_at).toLocaleDateString('en-IN');
          if (!groupedByDate[date]) {
            groupedByDate[date] = { success: 0, failed: 0 };
          }
          
          if (event.event_type === 'payment_success') {
            groupedByDate[date].success++;
          } else if (event.event_type === 'payment_failed') {
            groupedByDate[date].failed++;
          }
        });
        
        const chartData = Object.entries(groupedByDate).map(([date, counts]) => ({
          date,
          ...counts,
        }));
        
        setDailyData(chartData);
      }
    } catch (error) {
      console.error('Error fetching daily trends:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-8">
        <h1 className="text-3xl font-bold mb-8">Payment Analytics</h1>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-8 space-y-8">
      <h1 className="text-3xl font-bold">Payment Analytics</h1>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MbCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm text-muted-foreground">Success Rate</h3>
            {stats && stats.success_rate >= 85 ? (
              <TrendingUp className="w-5 h-5 text-primary" />
            ) : (
              <TrendingDown className="w-5 h-5 text-destructive" />
            )}
          </div>
          <p className="text-4xl font-bold text-primary">
            {stats?.success_rate.toFixed(1) || 0}%
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {stats?.successful_payments || 0} / {stats?.total_checkouts || 0} checkouts
          </p>
        </MbCard>
        
        <MbCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm text-muted-foreground">Total Revenue</h3>
            <DollarSign className="w-5 h-5 text-secondary" />
          </div>
          <p className="text-4xl font-bold text-secondary">
            ₹{stats?.total_revenue.toLocaleString('en-IN') || 0}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Last 30 days
          </p>
        </MbCard>
        
        <MbCard className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm text-muted-foreground">Avg. Transaction</h3>
            <DollarSign className="w-5 h-5 text-primary" />
          </div>
          <p className="text-4xl font-bold">
            ₹{Math.round(stats?.avg_transaction_value || 0)}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Per successful payment
          </p>
        </MbCard>
      </div>
      
      {/* Daily Trends Chart */}
      <MbCard className="p-6">
        <h2 className="text-xl font-bold mb-4">7-Day Trend</h2>
        {dailyData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="success" 
                stroke="hsl(var(--primary))" 
                strokeWidth={2}
                name="Successful Payments"
              />
              <Line 
                type="monotone" 
                dataKey="failed" 
                stroke="hsl(var(--destructive))" 
                strokeWidth={2}
                name="Failed Payments"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-muted-foreground text-center py-8">
            No payment data available for the last 7 days
          </p>
        )}
      </MbCard>
      
      {/* Health Status */}
      <MbCard className="p-6">
        <h2 className="text-xl font-bold mb-4">Payment Health</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Checkout Conversion</span>
            <span className={`font-semibold ${stats && stats.success_rate >= 85 ? 'text-primary' : 'text-destructive'}`}>
              {stats && stats.success_rate >= 85 ? '✓ Healthy' : '⚠ Needs Attention'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Failed Payments</span>
            <span className="font-semibold">
              {stats?.failed_payments || 0} attempts
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Average Order Value</span>
            <span className="font-semibold">
              ₹{Math.round(stats?.avg_transaction_value || 0)}
            </span>
          </div>
        </div>
      </MbCard>
    </div>
  );
}
